import { ApolloError } from '@apollo/client';
import { Alert, Box, Card, Center, Skeleton, VStack } from '@chakra-ui/react';
import { BellAlertIcon } from '@heroicons/react/24/outline';
import { useFocusedBranchContext } from '@packages/core/branch/focused-branch-context';
import { useTranslate } from '@tolgee/react';
import { ChartOptions, type ChartData } from 'chart.js';
import { useEffect, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import useOnlineOrderPaymentMethodMetrics from '../logic/use-online-order-payment-method-metrics';

export default function OnlineOrderPaymentMethodDistributionReport(props: { startTime: string; endTime: string }) {
  const branch = useFocusedBranchContext();
  const { t } = useTranslate();

  const { data, loading, error } = useOnlineOrderPaymentMethodMetrics(
    branch.branch?.id ?? '',
    props.startTime,
    props.endTime,
  );

  const [chartData, setChartData] = useState<ChartData<'doughnut'>>({ labels: [], datasets: [] });

  useEffect(() => {
    if (!data) {
      return;
    }

    const aggregateData = data.onlineOrderReport_owner_getPaymentMethodMetrics;

    const labels = [t('stripe', 'Stripe'), t('cash', 'Cash')];

    const chartValues = [aggregateData.stripeOrders, aggregateData.cashOrders];

    setChartData({
      labels: labels,
      datasets: [
        {
          label: t('orders', 'Orders'),
          data: chartValues,
          borderWidth: 2,
          borderRadius: 0,
          backgroundColor: ['rgba(99, 102, 241, 0.5)', 'rgba(16, 185, 129, 0.5)'], // Example colors for Stripe and Cash
        },
      ],
    });
  }, [data, setChartData, t]);

  if (loading) {
    return (
      <VStack gap={4} align="stretch">
        <Skeleton height="40px" />
        <Skeleton height="300px" />
      </VStack>
    );
  }

  if (error && error instanceof ApolloError) {
    return (
      <Alert.Root status="error" borderRadius="md">
        <BellAlertIcon className="w-4 h-4" />
        <Box>
          <Alert.Title>{t('Error loading payment method distribution')}</Alert.Title>
          <Alert.Description>{error.message}</Alert.Description>
        </Box>
      </Alert.Root>
    );
  }

  if (!data || data.onlineOrderReport_owner_getPaymentMethodMetrics.totalOrders === 0) {
    return null;
  }

  const options: ChartOptions<'doughnut'> = {
    responsive: true,
    interaction: {
      intersect: false,
      mode: 'index',
    },
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
  };

  return (
    <Card.Root>
      <Card.Header p={0}>
        <Card.Title fontWeight={'semibold'}>{t('Payment Method Distribution')}</Card.Title>
      </Card.Header>
      <Card.Body p={0}>
        <Center rounded={'xl'} mt={4} p={4} w="full" bgColor={'gray.50'} border={'1px solid'} borderColor={'gray.200'}>
          <Center>
            <Doughnut options={options} data={chartData} />
          </Center>
        </Center>
      </Card.Body>
    </Card.Root>
  );
}
